import type { SegmentWithId } from "../hooks/useSegments";

interface SegmentOption {
  id: string;
  label: string;
}

/**
 * Builds a flat list of segment options with indentation to show hierarchy.
 * Top-level segments appear unindented; children are indented with "—" prefixes.
 */
export function buildNestedSegmentOptions(segments: SegmentWithId[]): SegmentOption[] {
  const childrenMap = new Map<string | null, SegmentWithId[]>();
  for (const seg of segments) {
    const parentKey = seg.parentSegmentId || null;
    if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
    childrenMap.get(parentKey)!.push(seg);
  }

  const result: SegmentOption[] = [];

  function walk(parentId: string | null, depth: number) {
    const children = childrenMap.get(parentId) || [];
    for (const seg of children) {
      const indent = depth > 0 ? "\u00A0\u00A0".repeat(depth) + "└ " : "";
      result.push({ id: seg.id, label: `${indent}${seg.name}` });
      walk(seg.id, depth + 1);
    }
  }

  walk(null, 0);
  return result;
}

/**
 * Returns the IDs of a segment and all its descendants.
 * Useful for excluding circular parent references when editing.
 */
export function getSegmentAndDescendantIds(segments: SegmentWithId[], segmentId: string): string[] {
  const childrenMap = new Map<string, SegmentWithId[]>();
  for (const seg of segments) {
    const parentKey = seg.parentSegmentId || "";
    if (!childrenMap.has(parentKey)) childrenMap.set(parentKey, []);
    childrenMap.get(parentKey)!.push(seg);
  }

  const ids: string[] = [];
  function collect(id: string) {
    ids.push(id);
    for (const child of childrenMap.get(id) || []) {
      collect(child.id);
    }
  }
  collect(segmentId);
  return ids;
}
